import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, AfterViewInit, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GeolocationService } from '../services/geolocation.service';
import { LanguageService } from '../services/language.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { MaterialModule } from '../shared/material.module';
import * as L from 'leaflet';

export enum MapType {
  INCIDENT = 'incident'
}

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslatePipe
  ],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy {
  
  @Input() height: string = '400px';
  @Input() showControls: boolean = true;
  @Input() readonly: boolean = false;
  @Input() draggable: boolean = true;
  @Input() mapType: String = '';
  @Input() currentLocation: { lat: number, lng: number, address: string } | null = null;
  @Input() lastPosition: { lat: number, lng: number, address: string } | null = null;
  
  // Eventi separati per i due marker
  @Output() blueMarkerChanged = new EventEmitter<{ lat: number, lng: number, address: string }>();
  @Output() redMarkerChanged = new EventEmitter<{ lat: number, lng: number, address: string }>();
  @Output() locationSelected = new EventEmitter<{ lat: number, lng: number, address: string }>();
  @Output() currentLocationChanged = new EventEmitter<{ lat: number, lng: number, address: string }>();

  // Zoom configurabile (12 = più largo, si vedono entrambi i marker)
  private readonly DEFAULT_ZOOM = 12;
  private readonly LOCATION_ZOOM = 12;

  private center: { lat: number, lng: number } = { lat: 41.9, lng: 12.5 }; // Default: Roma
  private map: L.Map | null = null;
  
  // Due marker separati
  public blueMarker: L.Marker | null = null; // Marker blu (incident/last position)
  public redMarker: L.Marker | null = null;  // Marker rosso (current position)
  
  private defaultIcon: L.Icon;
  private incidentIcon: L.Icon;
  private currentLocationIcon: L.Icon;

  public blueMarkerAddress: string = '';
  public redMarkerAddress: string = '';
  public isLoading: boolean = false;
  public mapId: string;
  public isMobileView: boolean = false;

  // Stati marker e drag mappa
  public blueMarkerPosition: { lat: number, lng: number } | null = null;
  public redMarkerPosition: { lat: number, lng: number } | null = null;
  public isDraggingRedMarker: boolean = false;
  public isDraggingMap: boolean = false;

  constructor(
    private geolocationService: GeolocationService,
    private languageService: LanguageService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.mapId = `map-${Math.random().toString(36).substr(2, 9)}`;
    this.checkViewport();
    
    this.defaultIcon = L.icon({
      iconUrl: 'assets/marker-icon.svg',
      shadowUrl: 'assets/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    this.incidentIcon = L.icon({
      iconUrl: 'assets/marker-icon.svg', // Blu per sinistro/last position
      shadowUrl: 'assets/marker-shadow.png',
      iconSize: [35, 56],
      iconAnchor: [17, 56],
      popupAnchor: [1, -48],
      shadowSize: [56, 56]
    });

    this.currentLocationIcon = L.icon({
      iconUrl: 'assets/marker-icon-red.svg', // Rosso per posizione attuale
      shadowUrl: 'assets/marker-shadow.png',
      iconSize: [35, 56],
      iconAnchor: [17, 56],
      popupAnchor: [1, -48],
      shadowSize: [56, 56]
    });
  }

  @Input() set initialLocation(location: {lat: number, lng: number, address: string} | null) {
    if (location && !this.isDraggingRedMarker && !this.blueMarker) {
      this.center = { lat: location.lat, lng: location.lng };
      this.blueMarkerPosition = { lat: location.lat, lng: location.lng };
      this.blueMarkerAddress = location.address;

      if (this.map) {
        this.map.setView([location.lat, location.lng], this.LOCATION_ZOOM);
        this.updateBlueMarker();

        if (location.address) {
          setTimeout(() => {
            if (this.blueMarker) {
              this.blueMarker.bindPopup(this.createPopupContent('Posizione sinistro', location.address)).openPopup();
            }
          }, 100);
        }
      }
    }
  }

  @Input() set currentLocationInput(location: {lat: number, lng: number, address: string} | null) {
    this.currentLocation = location;
    if (location && !this.isDraggingRedMarker && !this.redMarker) {
      this.redMarkerPosition = { lat: location.lat, lng: location.lng };
      this.redMarkerAddress = location.address;
      if (this.map) {
        this.updateRedMarker();
      }
    }
  }

  @Input() set lastPositionInput(location: {lat: number, lng: number, address: string} | null) {
    this.lastPosition = location;
    if (location && !this.isDraggingRedMarker && !this.blueMarker) {
      this.blueMarkerPosition = { lat: location.lat, lng: location.lng };
      this.blueMarkerAddress = location.address;
      
      if (this.map) {
        this.map.setView([location.lat, location.lng], this.LOCATION_ZOOM);
        this.center = { lat: location.lat, lng: location.lng };
        this.updateBlueMarker();
      }
    }
  }

  ngOnInit() {
    this.setMapHeight();
    // Carico posizione GEO se permessi abilitati
    if (!this.readonly) {
      this.isLoading = true;
      this.geolocationService.getCurrentPositionAdvanced().subscribe({
        next: (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.redMarkerPosition = { lat, lng };
          this.getAddressForRedMarker(lat, lng);
          if (this.map) {
            this.updateRedMarker();
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.snackBar.open(this.languageService.translate('gps.notAvailable'), this.languageService.translate('notification.close'), { duration: 4000 });
        }
      });
    }
  }

  ngAfterViewInit() {
    this.initializeMap();
  }

  ngOnDestroy() {
    this.cleanupMap();
  }

  private initializeMap() {
    const mapElement = document.getElementById(this.mapId);
    if (!mapElement) {
      console.error('Map container not found:', this.mapId);
      return;
    }

    let initialLat = this.center.lat;
    let initialLng = this.center.lng;
    let initialZoom = this.DEFAULT_ZOOM;

    if (this.lastPosition) {
      initialLat = this.lastPosition.lat;
      initialLng = this.lastPosition.lng;
      initialZoom = this.LOCATION_ZOOM;
      this.center = { lat: initialLat, lng: initialLng };
    } else if (this.blueMarkerPosition) {
      initialLat = this.blueMarkerPosition.lat;
      initialLng = this.blueMarkerPosition.lng;
      initialZoom = this.LOCATION_ZOOM;
    } else if (this.redMarkerPosition) {
      initialLat = this.redMarkerPosition.lat;
      initialLng = this.redMarkerPosition.lng;
      initialZoom = this.LOCATION_ZOOM;
      this.center = { lat: initialLat, lng: initialLng };
    }

    try {
      this.map = L.map(this.mapId, {
        zoomControl: false,
        minZoom: 5,
        maxZoom: 19,
        preferCanvas: true,
        doubleClickZoom: !this.readonly,
        dragging: !this.readonly,
        touchZoom: !this.readonly,
        scrollWheelZoom: !this.readonly,
        boxZoom: !this.readonly,
        keyboard: !this.readonly
      }).setView([initialLat, initialLng], initialZoom);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      if (!this.readonly) {
        L.control.zoom({ position: 'topright' }).addTo(this.map);
      }

      this.initializeMarkers();

      // Gestione click sulla mappa
      if (!this.readonly) {
        this.map.on('click', (e: L.LeafletMouseEvent) => {
          if (e.originalEvent.target === this.map?.getContainer()) {
            this.onMapClick(e.latlng.lat, e.latlng.lng);
          }
        });
        // Imposto cursore mano solo mentre si trascina la mappa
        this.map.on('dragstart', () => {
          this.isDraggingMap = true;
          this.cdr.markForCheck();
        });
        this.map.on('dragend', () => {
          this.isDraggingMap = false;
          this.cdr.markForCheck();
        });
      }

      setTimeout(() => this.map?.invalidateSize(), 0);

    } catch (error) {
      console.error('Map initialization error:', error);
    }
  }

  private initializeMarkers() {
    if (this.blueMarkerPosition) {
      this.updateBlueMarker();
    }
    
    if (this.redMarkerPosition) {
      this.updateRedMarker();
    }
  }

  private onMapClick(lat: number, lng: number) {
    // Se non c'è il marker rosso, lo crea al primo click manulamente ( caso in cui non si ha i permessi di geo o la geo è fallita )
    if (!this.redMarker) {
      this.addRedMarker(lat, lng);
      this.getAddressForRedMarker(lat, lng);
    }
  }

  private addBlueMarker(lat: number, lng: number) {
    if (!this.map) return;
    
    this.blueMarkerPosition = { lat, lng };

    if (this.blueMarker) {
      this.map.removeLayer(this.blueMarker);
    }

    this.blueMarker = L.marker([lat, lng], {
      icon: this.incidentIcon,
      draggable: false 
    }).addTo(this.map);
  }

  private addRedMarker(lat: number, lng: number) {
    if (!this.map) return;
    
    this.redMarkerPosition = { lat, lng };

    // Rimuovo marker rosso esistente
    if (this.redMarker) {
      this.map.removeLayer(this.redMarker);
    }

    // Creo nuovo marker rosso
    this.redMarker = L.marker([lat, lng], {
      icon: this.currentLocationIcon,
      draggable: this.draggable && !this.readonly
    }).addTo(this.map);

    if (this.draggable && !this.readonly) {
      this.setupRedMarkerEvents();
    }
  }

  private updateBlueMarker() {
    if (this.blueMarkerPosition) {
      if (!this.blueMarker) {
        this.addBlueMarker(this.blueMarkerPosition.lat, this.blueMarkerPosition.lng);
        // Carico campo indirizzo se non è già presente
        if (!this.blueMarkerAddress) {
          this.getAddressForBlueMarker(this.blueMarkerPosition.lat, this.blueMarkerPosition.lng);
        }
      } else {
        this.blueMarker.setLatLng([this.blueMarkerPosition.lat, this.blueMarkerPosition.lng]);
      }
      if (this.blueMarkerAddress && this.blueMarker) {
        this.blueMarker.bindPopup(this.createPopupContent(this.languageService.translate('map.legend.vehiclePosition'), this.blueMarkerAddress));
      }
    }
  }

  private updateRedMarker() {
    if (this.redMarkerPosition && !this.isDraggingRedMarker) {
      if (!this.redMarker) {
        this.addRedMarker(this.redMarkerPosition.lat, this.redMarkerPosition.lng);
        // Carico campo indirizzo se non è già presente
        if (!this.redMarkerAddress) {
          this.getAddressForRedMarker(this.redMarkerPosition.lat, this.redMarkerPosition.lng);
        }
      } else {
        this.redMarker.setLatLng([this.redMarkerPosition.lat, this.redMarkerPosition.lng]);
      }
      if (this.redMarkerAddress && this.redMarker) {
        this.redMarker.bindPopup(this.createPopupContent(this.languageService.translate('map.legend.accidentPosition'), this.redMarkerAddress));
      }
    }
  }

  private setupRedMarkerEvents() {
    if (!this.redMarker) return;

    this.redMarker.on('dragstart', () => {
      this.isDraggingRedMarker = true;
      this.map?.off('click');
    });

    this.redMarker.on('drag', (event) => {
      const marker = event.target;
      const position = marker.getLatLng();
      this.redMarkerPosition = { lat: position.lat, lng: position.lng };
    });

    this.redMarker.on('dragend', (event) => {
      const marker = event.target;
      const position = marker.getLatLng();
      this.redMarkerPosition = { lat: position.lat, lng: position.lng };
      this.getAddressForRedMarker(position.lat, position.lng);
      
      setTimeout(() => {
        this.isDraggingRedMarker = false;
        this.setupMapClickEvents();
      }, 100);
    });
  }

  private setupMapClickEvents() {
    if (!this.readonly && this.map) {
      this.map.on('click', (e: L.LeafletMouseEvent) => {
        if (e.originalEvent.target === this.map?.getContainer()) {
          this.onMapClick(e.latlng.lat, e.latlng.lng);
        }
      });
    }
  }

  private getAddressForBlueMarker(lat: number, lng: number) {
    this.isLoading = true;
    this.geolocationService.getAddress(lat, lng).subscribe({
      next: (address) => {
        this.blueMarkerAddress = address;
        this.isLoading = false;
        if (this.blueMarker) {
          this.blueMarker.bindPopup(this.createPopupContent(this.languageService.translate('map.legend.vehiclePosition'), address)).openPopup();
        }

        // Emetto evento per marker blu (sinistro)
        this.blueMarkerChanged.emit({ lat, lng, address });
        this.locationSelected.emit({ lat, lng, address });
      },
      error: () => {
        this.blueMarkerAddress = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
        this.isLoading = false;
        this.blueMarkerChanged.emit({ lat, lng, address: this.blueMarkerAddress });
        this.locationSelected.emit({ lat, lng, address: this.blueMarkerAddress });
      }
    });
  }

  private getAddressForRedMarker(lat: number, lng: number) {
    this.isLoading = true;
    this.geolocationService.getAddress(lat, lng).subscribe({
      next: (address) => {
        this.redMarkerAddress = address;
        this.isLoading = false;
        if (this.redMarker) {
          this.redMarker.bindPopup(this.createPopupContent(this.languageService.translate('map.legend.accidentPosition'), address)).openPopup();
        }

        // Emetto evento per marker rosso (posizione attuale)
        this.redMarkerChanged.emit({ lat, lng, address });
        this.currentLocationChanged.emit({ lat, lng, address });
      },
      error: () => {
        this.redMarkerAddress = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`;
        this.isLoading = false;
        this.redMarkerChanged.emit({ lat, lng, address: this.redMarkerAddress });
        this.currentLocationChanged.emit({ lat, lng, address: this.redMarkerAddress });
      }
    });
  }

  private createPopupContent(title: string, address: string): string {
    return `
      <div class="map-popup">
        <strong>${title}</strong><br>
        ${address}
      </div>
    `;
  }

  private cleanupMap() {
    if (this.map) {
      this.map.off();
      this.map.remove();
      this.map = null;
    }
    // Reset dei marker
    this.blueMarker = null;
    this.redMarker = null;
  }

  private checkViewport() {
    this.isMobileView = window.innerWidth <= 768;
  }

  private setMapHeight() {
    if (this.isMobileView) {
      this.height = (window.innerHeight * 0.4) + 'px';
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.checkViewport();
    this.setMapHeight();
    setTimeout(() => this.map?.invalidateSize(), 100);
  }

  public updateMap(location: { lat: number, lng: number, address: string }) {
    if (this.map) {
      this.center = { lat: location.lat, lng: location.lng };
      this.blueMarkerPosition = { lat: location.lat, lng: location.lng };
      this.blueMarkerAddress = location.address;
      this.updateBlueMarker();
      this.map.setView([location.lat, location.lng], this.LOCATION_ZOOM);
    }
  }

  /*copyBlueAddress() {
    if (this.blueMarkerAddress && navigator.clipboard) {
      navigator.clipboard.writeText(this.blueMarkerAddress).then(() => {
        this.snackBar.open('Indirizzo marker blu copiato', 'Chiudi', { duration: 1500 });
      });
    }
  }

  copyRedAddress() {
    if (this.redMarkerAddress && navigator.clipboard) {
      navigator.clipboard.writeText(this.redMarkerAddress).then(() => {
        this.snackBar.open('Indirizzo marker rosso copiato', 'Chiudi', { duration: 1500 });
      });
    }
  }*/

  // Metodi per ottenere le posizioni attuali
  getBlueMarkerPosition(): { lat: number, lng: number, address: string } | null {
    if (this.blueMarkerPosition) {
      return {
        lat: this.blueMarkerPosition.lat,
        lng: this.blueMarkerPosition.lng,
        address: this.blueMarkerAddress
      };
    }
    return null;
  }

  getRedMarkerPosition(): { lat: number, lng: number, address: string } | null {
    if (this.redMarkerPosition) {
      return {
        lat: this.redMarkerPosition.lat,
        lng: this.redMarkerPosition.lng,
        address: this.redMarkerAddress
      };
    }
    return null;
  }

  setBlueMarkerPosition(lat: number, lng: number) {
    this.addBlueMarker(lat, lng);
    this.getAddressForBlueMarker(lat, lng);
  }

  setRedMarkerPosition(lat: number, lng: number) {
    this.addRedMarker(lat, lng);
    this.getAddressForRedMarker(lat, lng);
  }

  // Metodi per rimuovere i marker
  removeBlueMarker() {
    if (this.blueMarker && this.map) {
      this.map.removeLayer(this.blueMarker);
      this.blueMarker = null;
      this.blueMarkerPosition = null;
      this.blueMarkerAddress = '';
    }
  }

  removeRedMarker() {
    if (this.redMarker && this.map) {
      this.map.removeLayer(this.redMarker);
      this.redMarker = null;
      this.redMarkerPosition = null;
      this.redMarkerAddress = '';
    }
  }
}